import { useCallback, useState } from "react";
import { upsertEvents } from "../api/events";
import { computeSchedule } from "../lib/calc";

const useInlineEdit = (events, setEvents, projectStartAt, projectId, userId) => {
  const [editingCell, setEditingCell] = useState(null);
  const [draftValue, setDraftValue] = useState("");
  const [error, setError] = useState("");

  const handleStartEdit = useCallback((id, field, currentValue) => {
    setEditingCell({ id, field });
    setDraftValue(currentValue ?? "");
  }, []);

  const handleCommitEdit = useCallback(async () => {
    if (!editingCell) return;
    const { id, field } = editingCell;

    const updated = events.map((e) =>
      e.id === id ? { ...e, [field]: draftValue } : e
    );
    const recomputed = computeSchedule(projectStartAt, updated);

    // Diff: only persist rows that actually changed
    const changed = recomputed.filter((newEvent, i) => {
      const old = events[i];
      if (!old) return true;
      if (newEvent.id === id) return true;
      return newEvent.startAt !== old.startAt || newEvent.endAt !== old.endAt;
    });

    setEvents(recomputed);
    setEditingCell(null);
    setDraftValue("");

    try {
      if (changed.length) {
        await upsertEvents(changed, projectId, userId);
      }
    } catch (err) {
      setError(err.message || "Failed to update event");
    }
  }, [editingCell, draftValue, events, setEvents, projectStartAt, projectId, userId]);

  const handleCancelEdit = useCallback(() => {
    setEditingCell(null);
    setDraftValue("");
  }, []);

  return {
    editingCell,
    draftValue,
    setDraftValue,
    error,
    handleStartEdit,
    handleCommitEdit,
    handleCancelEdit,
  };
};

export default useInlineEdit;
